import { useCallback } from 'react';

import clsxm from '@/lib/clsxm';

import { card, tone } from '@/components/dashboard/ui';
import { nav } from '@/components/layout/nav/navUi';

import { useAnalytics } from '@/contexts/Analytics';

/**
 * Keyboard focus ring. The outline style is an arbitrary property on purpose:
 * `clsxm` (tailwind-merge) drops a plain `outline` next to `outline-2`, and
 * without a style no outline is drawn. Keep it this way when you merge it.
 */
const focusRing =
  'focus-visible:[outline-style:solid] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600';
/** The same ring drawn inside, for rows in a scroll box that would clip it. */
const focusInset =
  'focus-visible:[outline-style:solid] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-600';

/**
 * Marky chat's design kit: the dashboard kit's colour roles (`dashboard/ui.tsx`)
 * and the navigation kit's rows (`layout/nav/navUi.tsx`) applied to the chat.
 * Light mode is a quiet indigo-tinted neutral with white cards; dark mode stays
 * on the app's slate, like the sidebar. UI text is 13 px (rows, chips, meta),
 * 14 px (buttons, the composer toolbar) and 15 px (messages, the composer).
 */
export const chat = {
  ...tone,
  /** The page behind the transcript and the panels. */
  canvas: tone.page,
  /** The sidebar, the rail and the phone drawer. */
  panel: nav.panel,
  hairline: nav.hairline,
  /** Placeholder text, inactive icons. */
  faint: 'text-[#8b8ba0] dark:text-slate-500',
  /** Text of an assistant reply: a step softer than headings. */
  prose: 'text-[#26263a] dark:text-slate-200',
  hover: nav.hover,
  active: nav.active,
  focus: focusRing,
  /** Focus ring drawn inside, for rows in a scroll box that would clip it. */
  focusInset,
};

/** A raised white card with a hairline and a faint indigo shadow. */
export const chatCard = clsxm(card, 'rounded-2xl');

/** A card that sits inside a message (tool results, files, decks). */
export const resultCard = clsxm(
  'overflow-hidden rounded-xl bg-white ring-1 ring-[#0f0f28]/[0.08] shadow-[0_1px_2px_rgba(16,16,40,0.04),0_8px_24px_-16px_rgba(49,46,129,0.25)]',
  'dark:bg-slate-800/70 dark:ring-white/[0.08] dark:shadow-none',
);

/** The composer box: the page's one strong shadow. */
export const composerCard = clsxm(
  'rounded-[22px] bg-white ring-1 ring-[#0f0f28]/[0.08] shadow-[0_1px_2px_rgba(16,16,40,0.05),0_18px_40px_-24px_rgba(49,46,129,0.45)] transition-shadow',
  'focus-within:ring-[#0f0f28]/[0.14] focus-within:shadow-[0_1px_2px_rgba(16,16,40,0.06),0_22px_48px_-22px_rgba(49,46,129,0.55)]',
  'dark:bg-slate-800 dark:ring-white/10 dark:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.8)] dark:focus-within:ring-white/20',
);

/** Floating menus and popovers. */
export const menuPanel = clsxm(
  'rounded-xl bg-white p-1 ring-1 ring-[#0f0f28]/[0.08] shadow-[0_12px_32px_-12px_rgba(16,16,40,0.25),0_2px_6px_rgba(16,16,40,0.06)] focus:outline-none',
  'dark:bg-slate-800 dark:ring-white/10 dark:shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]',
);

/** One row of a menu (`menuPanel`). */
export const menuItem = clsxm(
  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-[13px] font-medium transition-colors',
  'text-[#26263a] hover:bg-[#f4f4fb] hover:text-[#10101c] dark:text-slate-200 dark:hover:bg-white/[0.06] dark:hover:text-white',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

/** Icon of a menu row. */
export const menuIcon = 'h-4 w-4 shrink-0 text-[#8b8ba0] dark:text-slate-400';

/** Square icon button (header, toolbars, panel chrome). */
export const iconButton = clsxm(
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
  'text-[#6f6f86] hover:bg-[#0f0f28]/[0.05] hover:text-[#10101c] dark:text-slate-400 dark:hover:bg-white/[0.07] dark:hover:text-white',
  'disabled:cursor-not-allowed disabled:opacity-40',
  focusRing,
);

/** An icon button that is switched on (an open panel). */
export const iconButtonOn =
  'bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800 dark:bg-primary-500/15 dark:text-primary-200 dark:hover:bg-primary-500/25 dark:hover:text-primary-100';

/** Quiet white pill (model picker, mode chips, secondary actions). */
export const pill = clsxm(
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition',
  'bg-white text-[#26263a] ring-1 ring-[#0f0f28]/[0.08] hover:text-[#10101c] hover:ring-[#0f0f28]/20',
  'dark:bg-white/[0.05] dark:text-slate-200 dark:ring-white/10 dark:hover:text-white dark:hover:ring-white/25',
  focusRing,
);

/** A pill that is switched on (the picked mode). */
export const pillOn =
  'bg-primary-50 text-primary-700 ring-primary-200 hover:text-primary-800 hover:ring-primary-300 dark:bg-primary-500/15 dark:text-primary-200 dark:ring-primary-400/30 dark:hover:text-primary-100 dark:hover:ring-primary-400/50';

/** The page's primary button: filled indigo. */
export const buttonPrimary = clsxm(
  'inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-600 px-4 py-2 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] transition-colors hover:bg-primary-700',
  'disabled:cursor-not-allowed disabled:opacity-50',
  focusRing,
);

/** Secondary button beside it. */
export const buttonSecondary = clsxm(
  'inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#10101c] ring-1 ring-[#0f0f28]/10 transition hover:ring-[#0f0f28]/25',
  'dark:bg-white/[0.06] dark:text-white dark:ring-white/15 dark:hover:ring-white/30',
  'disabled:cursor-not-allowed disabled:opacity-50',
  focusRing,
);

/** Quiet text button (Dismiss, Cancel). */
export const buttonGhost = clsxm(
  'inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-medium transition-colors',
  'text-[#56566d] hover:bg-[#0f0f28]/[0.05] hover:text-[#10101c] dark:text-slate-300 dark:hover:bg-white/[0.07] dark:hover:text-white',
  focusRing,
);

/** A compact step row in a reply (tool calls, thinking). */
export const stepRow = clsxm(
  'group flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-start text-[13px] transition-colors',
  'text-[#56566d] hover:bg-[#0f0f28]/[0.04] hover:text-[#10101c] dark:text-slate-400 dark:hover:bg-white/[0.05] dark:hover:text-slate-100',
  focusRing,
);

/** The small tile holding a step's icon. */
export const stepIcon = clsxm(
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white ring-1 ring-[#0f0f28]/[0.08] text-[#6f6f86]',
  'dark:bg-white/[0.06] dark:ring-white/10 dark:text-slate-400',
);

/** Status banners in the transcript. */
export const banner = {
  base: 'flex w-full items-start gap-3 rounded-xl px-3.5 py-3 text-[13px] leading-5 ring-1',
  info: 'bg-primary-50/70 text-primary-900 ring-primary-200/70 dark:bg-primary-500/10 dark:text-primary-100 dark:ring-primary-400/20',
  warn: 'bg-amber-50 text-amber-900 ring-amber-200/80 dark:bg-amber-500/10 dark:text-amber-100 dark:ring-amber-400/20',
  error:
    'bg-red-50 text-red-900 ring-red-200/80 dark:bg-red-500/10 dark:text-red-100 dark:ring-red-400/20',
};

/** Section label in lists (sidebar groups, menus). */
export const sectionLabel = clsxm(
  'px-2.5 pb-1 text-[12px] font-medium',
  nav.quiet,
);

/** Column the transcript and the composer share. */
export const readingColumn = 'mx-auto w-full max-w-[46rem]';

/**
 * Click tracking for the chat. PostHog autocapture is off, so these are the
 * only way to see which chat features people use: one event, `Marky Chat`,
 * with `action` (`send`, `stop`, `new_chat`, `open_chat`, `mode`, `model`,
 * `attach`, `voice`, `panel`, `share`, `sidebar`, …) and its details.
 */
export function useChatTrack() {
  const analytics = useAnalytics();
  return useCallback(
    (action: string, extra?: Record<string, unknown>) => {
      // A detail named `action` (deck, document, queue) would replace the
      // event's action: it travels as `detail` instead.
      const { action: detail, ...rest } = extra ?? {};
      analytics.track('Marky Chat', {
        ...rest,
        ...(detail === undefined ? {} : { detail }),
        action,
        viewport:
          typeof window === 'undefined'
            ? undefined
            : window.innerWidth < 768
              ? 'phone'
              : window.innerWidth < 1024
                ? 'tablet'
                : 'desktop',
      });
    },
    [analytics],
  );
}
